import React, { useRef, useState } from 'react';
import { PrivateBlock, BookingType } from '../types/types';

interface PrintPrivateEventsProps {
  privateBlocks: PrivateBlock[];
}

// Helper function to translate service types to Hungarian
const translateServiceType = (type: BookingType): string => {
  switch (type) {
    case 'gokart': return 'Gokart';
    case 'vr': return 'VR Élmény';
    case 'simulator': return 'Szimulátor';
    case 'playhouse': return 'Játszóház';
    default: return type;
  }
};

// Helper function to format date to Hungarian format
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  });
};

const PrintPrivateEvents: React.FC<PrintPrivateEventsProps> = ({ privateBlocks }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const originalContents = document.body.innerHTML;
    const printContents = printContent.innerHTML;
    
    document.body.innerHTML = `
      <div style="padding: 20px;">
        <h1 style="text-align: center; margin-bottom: 20px;">Zártkörű Események</h1>
        ${printContents}
      </div>
    `;
    
    window.print();
    document.body.innerHTML = originalContents;
  };

  // Filter blocks based on type and date range
  const filteredBlocks = privateBlocks.filter(block => {
    const matchesType = filterType === 'all' || block.serviceType === filterType;
    
    const blockDate = new Date(block.date);
    const matchesStartDate = !startDate || blockDate >= new Date(startDate);
    const matchesEndDate = !endDate || blockDate <= new Date(endDate);
    
    return matchesType && matchesStartDate && matchesEndDate;
  });

  // Group events by date
  const eventsByDate: Record<string, PrivateBlock[]> = {};
  filteredBlocks.forEach(block => {
    if (!eventsByDate[block.date]) {
      eventsByDate[block.date] = [];
    }
    eventsByDate[block.date].push(block);
  });

  // Sort dates
  const sortedDates = Object.keys(eventsByDate).sort((a, b) => {
    return new Date(a).getTime() - new Date(b).getTime();
  });

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Zártkörű Események Nyomtatása</h2>
        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Nyomtatás
        </button>
      </div>

      {/* Filter options */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Szolgáltatás típusa
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="all">Összes szolgáltatás</option>
            <option value="gokart">Gokart</option>
            <option value="vr">VR Élmény</option>
            <option value="simulator">Szimulátor</option>
            <option value="playhouse">Játszóház</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kezdő dátum
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Záró dátum
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      </div>

      <div ref={printRef} className="print-container">
        {sortedDates.length > 0 ? (
          sortedDates.map(date => (
            <div key={date} className="mb-6 print-section">
              <h3 className="text-md font-medium text-gray-800 mb-2 print-date">
                {formatDate(date)}
              </h3>
              <div className="overflow-hidden border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Szolgáltatás
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Időtartam
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Leírás
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider no-print">
                        Belső jegyzetek
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {eventsByDate[date].map((block, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {translateServiceType(block.serviceType)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {block.startHour}:00 - {block.endHour}:00
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {block.description || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 no-print">
                          {(block as any).notes || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            {privateBlocks.length > 0 
              ? 'A szűrési feltételeknek megfelelő zártkörű események nem találhatóak.'
              : 'Nincsenek zártkörű események beállítva.'}
          </div>
        )}
      </div>

      {/* Print-only styles */}
      <style>
        {`
          @media print {
            .print-container {
              font-size: 12pt;
            }
            .print-section {
              page-break-inside: avoid;
              margin-bottom: 20px;
            }
            .print-date {
              font-size: 14pt;
              margin-bottom: 10px;
            }
            .no-print {
              display: none;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PrintPrivateEvents;